import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ScheduledReportLoginType } from 'dashboard/enums/scheduled-report-login-type.enum';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { updateTreeValidity } from 'dynamic-form/util/update-tree-validity';
import { NumberValidator } from 'dynamic-form/validators/number-validator';
import { catchError, Observable } from 'rxjs';
import { BaseModal } from 'shared/abstract/base-modal';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { LoaderComponent } from 'shared/components/loader/loader.component';
import { MultiInputControlComponent } from 'shared/components/multi-input-control/multi-input-control.component';
import { IfNotDisabledDirective } from 'shared/directives/if-not-disabled.directive';
import { ListItem } from 'shared/interfaces/list-item';
import { ModalWithCloseLogic } from 'shared/interfaces/modal-with-close-logic.interface';
import { FormDifferService, ModalService, ToastService } from 'shared/services';
import { ObjectRequiredValidator } from 'shared/validators/object-required.validator';

@Component({
    selector: 'app-scheduled-reports-sftp-settings-modal',
    templateUrl: './scheduled-reports-sftp-settings-modal.component.html',
    styleUrls: ['./scheduled-reports-sftp-settings-modal.component.scss'],
    imports: [
        FormErrorComponent,
        MultiInputControlComponent,
        TranslateModule,
        ReactiveFormsModule,
        IfNotDisabledDirective,
        LoaderComponent,
    ],
})
export class ScheduledReportsSftpSettingsModalComponent extends BaseModal implements OnInit, ModalWithCloseLogic {
    public data: any = {};
    public id?: string;
    public form: FormGroup = new FormGroup({
        sftpSiteName: new FormControl<string[]>([], {
            validators: [ObjectRequiredValidator(), this.sftpSiteNameDuplicateValidator.bind(this)],
            nonNullable: true,
        }),
        host: new FormControl<string>('', [Validators.required]),
        port: new FormControl<number>(22, [Validators.required, NumberValidator({ step: 1, noScientific: true })]), // attributes on the HTML element provide the other validations
        loginType: new FormControl<ScheduledReportLoginType>(ScheduledReportLoginType.PASSWORD),
        username: new FormControl<string>('', [Validators.required]),
        password: new FormControl<string>('', [Validators.required]),
        key: new FormControl<string>({ value: '', disabled: true }, [Validators.required]),
    });
    public sftpSites: any[] = [];
    public currentSftpSiteName?: string;
    public readonly ScheduledReportLoginType: any = ScheduledReportLoginType;

    constructor(
        private dashboardService: DashboardService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private formDifferService: FormDifferService,
        private modalService: ModalService,
    ) {
        super();
    }

    public ngOnInit() {
        this.sftpSites = this.data.sftpData;
        this.formDifferService.registerForm(this.form);

        if (this.data.id) {
            this.form.get('password')!.disable();
            this.form.get('loginType')!.disable();
            this.form.get('key')!.disable();

            this.dashboardService
                .getSftpSettingsForId(this.data.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response: any) => {
                    response['sftpSiteName'] = [this.data.id];
                    this.id = this.data.id;
                    this.form.patchValue(response);
                    this.formDifferService.createDataSnapshot();
                });
        }

        this.formDifferService.createDataSnapshot();
    }

    public closeModal() {
        this.closeModalInstance();
    }

    public canDeactivateModal?(): boolean | Observable<boolean> {
        if (this.formDifferService.hasDataChanged()) {
            return this.modalService.openSimpleModal('dashboard.scheduledReports.sftpSettings.modal.unsavedChanges').onClose;
        } else {
            return true;
        }
    }

    public onLoginTypeChange() {
        const value = this.form.get('loginType')!.value;
        const passwordControl = this.form.get('password')!;
        const keyControl = this.form.get('key')!;

        keyControl.disable();
        passwordControl.disable();

        if (value == ScheduledReportLoginType.PASSWORD) {
            passwordControl.enable();
            passwordControl.markAsUntouched();
        } else if (value == ScheduledReportLoginType.KEY) {
            keyControl.enable();
            keyControl.markAsUntouched();
        }
    }

    public onSftpSiteNameChange([site]: ListItem[]) {
        this.currentSftpSiteName = site.label;
        this.id = undefined;

        // In the case of user generated values, there is no ID so the value is used as ID
        // So if they're not the same it means the user selected a pre-existing value
        if (site.id !== site.label) {
            this.id = site.id;
            this.dashboardService
                .getSftpSettingsForId(site.id)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    catchError((error) => {
                        this.form.get('sfptSiteName')!.setValue([]);
                        throw error;
                    }),
                )
                .subscribe((data: any) => {
                    // Remove null properties from data
                    Object.keys(data).forEach((key) => {
                        if (data[key] === null) {
                            delete data[key];
                        }
                    });

                    // We remove this since changing it was what triggered this function in the first place
                    delete data.sftpSiteName;

                    this.form.patchValue(data);
                    this.onLoginTypeChange(); // The above patchValue will not trigger the change event
                });
        }
    }

    public onDetailsChange($event: Event) {
        if (this.id) {
            this.form.get('sftpSiteName')!.setValue([]);
            this.form.get('sftpSiteName')!.markAsUntouched();
            this.id = undefined;
            this.currentSftpSiteName = undefined;
        }
    }

    public saveSftpSettings() {
        updateTreeValidity(this.form);
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        if (!this.formDifferService.hasDataChanged()) {
            this.closeModalInstance();
            return;
        }

        if (!this.id) {
            this.dashboardService
                .saveSftpData({ ...this.form.value, sftpSiteName: [this.currentSftpSiteName] }, this.data.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => {
                    this.formDifferService.createDataSnapshot();
                    this.closeModalInstance({
                        name: this.currentSftpSiteName,
                        id: response.id ?? this.data.id,
                    });
                });
        } else {
            this.formDifferService.createDataSnapshot();
            this.closeModalInstance({
                name: this.currentSftpSiteName,
                id: this.id,
            });
        }
    }

    public testSftpSettings() {
        updateTreeValidity(this.form);
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        this.dashboardService
            .testSftpSettings({ id: this.data.id, data: this.form.value })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.toastService.showToast('dashboard.scheduledReports.sftpSettings.test.success', 'success');
            });
    }

    public sftpSiteNameDuplicateValidator(control: AbstractControl) {
        const value = control.value?.[0];

        if (!value) {
            return null;
        }

        if (!this.id) {
            if (this.sftpSites.findIndex((sftp) => sftp.siteName === control.value[0]) !== -1) {
                return { duplicate: true };
            }
        }

        return null;
    }
}
